import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { DollarSign, Package, ClipboardList, MessageSquare, AlertTriangle, ArrowRight, CheckCircle2, Circle, ListChecks } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '@/lib/api';

const Stat = ({ icon: Icon, label, value, to, testid }) => (
  <Link to={to} className="bg-white rounded-2xl p-6 border border-forest/10 shadow-card hover:shadow-hover transition-shadow" data-testid={testid}>
    <div className="w-11 h-11 rounded-full bg-forest/10 flex items-center justify-center mb-4"><Icon className="w-5 h-5 text-forest" /></div>
    <p className="font-playfair text-3xl font-bold text-dark">{value}</p>
    <p className="font-manrope text-sm text-dark/60 mt-1">{label}</p>
  </Link>
);

function OnboardingChecklist() {
  const [checklist, setChecklist] = useState(null);

  useEffect(() => { api.get('/farm/onboarding-checklist').then((r) => setChecklist(r.data)).catch(() => {}); }, []);

  if (!checklist) return null;
  const pct = Math.round((checklist.completed_count / checklist.total_count) * 100);

  return (
    <div className="bg-white rounded-2xl p-6 md:p-8 border border-forest/10 mb-8" data-testid="onboarding-checklist-card">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2"><ListChecks className="w-5 h-5 text-forest" /><h2 className="font-playfair text-xl font-bold text-dark">Farm Partner Onboarding</h2></div>
        {checklist.all_complete && <span className="px-3 py-1 rounded-full text-xs font-bold bg-forest/10 text-forest" data-testid="onboarding-complete-badge">All set!</span>}
      </div>
      <p className="font-manrope text-sm text-dark/60 mb-4" data-testid="onboarding-progress-text">
        {checklist.all_complete ? "You've completed all onboarding steps." : `${checklist.completed_count} of ${checklist.total_count} onboarding steps complete`}
      </p>
      <div className="h-2 bg-cream rounded-full overflow-hidden mb-6">
        <div className="h-full bg-forest transition-all duration-500" style={{ width: `${pct}%` }} data-testid="onboarding-progress-bar" />
      </div>
      <div className="grid sm:grid-cols-2 gap-3">
        {checklist.items.map((item) => (
          <Link key={item.key} to={item.link} className="flex items-center justify-between gap-3 p-3 rounded-xl border border-forest/10 hover:bg-cream transition-colors" data-testid={`onboarding-item-${item.key}`}>
            <div className="flex items-center gap-3">
              {item.complete ? <CheckCircle2 className="w-5 h-5 text-forest shrink-0" /> : <Circle className="w-5 h-5 text-dark/25 shrink-0" />}
              <div>
                <p className="font-manrope text-sm font-semibold text-dark">{item.label}</p>
                {item.sub && (
                  <p className="font-manrope text-xs text-dark/50">
                    {item.sub.map((s) => `${s.label}${s.complete ? ' ✓' : ''}`).join(' · ')}
                  </p>
                )}
              </div>
            </div>
            <ArrowRight className="w-4 h-4 text-dark/30 shrink-0" />
          </Link>
        ))}
      </div>
    </div>
  );
}

export default function FarmDashboard() {
  const [data, setData] = useState(null);

  const load = () => api.get('/farm/dashboard').then((r) => setData(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const confirm = async (id) => { await api.post(`/farm/orders/${id}/confirm`, { partial: false }); toast.success('Order confirmed'); load(); };

  if (!data) return <div className="flex justify-center py-20"><div className="animate-spin rounded-full h-10 w-10 border-b-2 border-forest" /></div>;

  return (
    <div data-testid="farm-dashboard">
      <p className="font-manrope text-dark/60 mb-6">Welcome back, {data.farm.name}. Here's what's happening with your farm.</p>

      <OnboardingChecklist />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6 mb-8">
        <Stat icon={DollarSign} label="FarmBox owes your farm" value={`$${data.amount_owed.toFixed(2)}`} to="/farm/payments" testid="stat-owed" />
        <Stat icon={Package} label="Active products" value={data.active_products} to="/farm/products" testid="stat-products" />
        <Stat icon={ClipboardList} label="Orders awaiting confirmation" value={data.awaiting_orders.length} to="/farm/orders" testid="stat-orders" />
        <Stat icon={MessageSquare} label="Unread messages" value={data.unread_messages} to="/farm/messages" testid="stat-messages" />
      </div>

      {data.availability_warnings > 0 && (
        <div className="flex items-center gap-3 bg-gold/10 border border-gold/30 rounded-xl p-4 mb-8" data-testid="availability-warning">
          <AlertTriangle className="w-5 h-5 text-gold-dark" />
          <p className="font-manrope text-sm text-dark">{data.availability_warnings} product(s) marked unavailable. <Link to="/farm/availability" className="text-forest font-medium underline">Update availability</Link></p>
        </div>
      )}

      <div className="bg-white rounded-2xl p-6 md:p-8 border border-forest/10">
        <div className="flex items-center justify-between mb-6">
          <h2 className="font-playfair text-2xl font-bold text-dark">This Week From Your Farm</h2>
          <Link to="/farm/orders" className="text-forest font-manrope text-sm font-medium flex items-center gap-1">View orders <ArrowRight className="w-4 h-4" /></Link>
        </div>
        {!data.next_order ? (
          <p className="font-manrope text-dark/50">No open FarmBox orders right now. We'll notify you when a new weekly order is ready.</p>
        ) : (
          <div data-testid="this-week-order">
            <div className="space-y-2 mb-5">
              {data.next_order.items.map((it) => (
                <div key={it.product_id} className="flex items-center justify-between py-3 border-b border-forest/5">
                  <span className="font-manrope text-dark">{it.name}</span>
                  <span className="font-manrope font-semibold text-dark">{it.requested_quantity} {it.unit} requested</span>
                </div>
              ))}
            </div>
            <div className="flex flex-wrap items-center justify-between gap-4">
              <p className="font-playfair text-xl font-bold text-forest">Estimated FarmBox total — ${data.next_order.total.toFixed(2)}</p>
              <div className="flex gap-3">
                <button onClick={() => confirm(data.next_order.order_id)} className="btn-primary py-2.5 px-6 text-sm" data-testid="dash-confirm-order"><CheckCircle2 className="w-4 h-4 mr-2" />Confirm Order</button>
                <Link to="/farm/orders" className="btn-secondary py-2.5 px-6 text-sm" data-testid="dash-report-shortage">Report Shortage</Link>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
